import React, { useState } from "react";
import { T } from "./theme";
import { SectionHeader, Card, Input, Btn, Note, Avatar } from "./ui";
import { Icon } from "./Icons";

// ═══════════════════════════════════════════════════════════════════════
// ASSISTENTE DE IA — placeholder para integração futura com Claude API
// ═══════════════════════════════════════════════════════════════════════

const SUGESTOES = [
  "Como abordar um cliente de salão que ainda não decidiu?",
  "Quais argumentos usar para uma barbearia premium?",
  "Roteiro de primeira conversa com clínica estética",
  "Como contornar objeção de preço sem descontos?",
];

export default function AssistenteIA() {
  const [historico, setHistorico] = useState([]);
  const [pergunta, setPergunta] = useState("");
  const [loading, setLoading] = useState(false);

  function enviar(msg) {
    const texto = (msg || pergunta).trim();
    if (!texto) return;

    const nova = { role: "user", content: texto };
    setHistorico(h => [...h, nova]);
    setPergunta("");
    setLoading(true);

    // Simulação de resposta — substituir por chamada à Claude API
    setTimeout(() => {
      const resposta = gerarRespostaMock(texto);
      setHistorico(h => [...h, { role: "assistant", content: resposta }]);
      setLoading(false);
    }, 900);
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
      <SectionHeader
        title="Assistente de IA"
        subtitle="Tire dúvidas, peça roteiros de abordagem, gere ideias de argumentação"
        icon={<Icon.ai size={24} />}
      />

      <Note tone="info" icon={<Icon.info size={14} />}>
        <b>Assistente em modo demonstração.</b> Para ativar a integração com o Claude oficial, configure a chave de API no backend. Por ora, as respostas são simuladas.
      </Note>

      {/* Conversa */}
      <Card style={{ minHeight: 400, display: "flex", flexDirection: "column" }}>
        <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 14, overflowY: "auto", maxHeight: 500 }}>
          {historico.length === 0 && (
            <div style={{ padding: "30px 10px", textAlign: "center" }}>
              <div style={{ fontSize: 11, color: T.textSub, letterSpacing: 1, textTransform: "uppercase", fontWeight: 600, marginBottom: 18 }}>
                Sugestões para começar
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8, maxWidth: 500, margin: "0 auto" }}>
                {SUGESTOES.map((s, i) => (
                  <button key={i} onClick={() => enviar(s)} style={{
                    background: T.cardSoft, color: T.text,
                    border: `1px solid ${T.border}`, borderRadius: T.r.md,
                    padding: "10px 14px", fontSize: 12, textAlign: "left",
                    cursor: "pointer", transition: `all 160ms ${T.ease}`,
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = T.elev}
                  onMouseLeave={e => e.currentTarget.style.background = T.cardSoft}
                  >
                    💭 {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          {historico.map((msg, i) => (
            <div key={i} style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
              <Avatar initials={msg.role === "user" ? "EU" : "IA"} size={28} tone={msg.role === "user" ? T.info : T.brand} />
              <div style={{
                flex: 1, background: msg.role === "user" ? T.infoSoft : T.input,
                border: `1px solid ${msg.role === "user" ? T.info + "30" : T.border}`,
                borderRadius: T.r.md, padding: "10px 14px", fontSize: 13, lineHeight: 1.6,
                color: T.text, whiteSpace: "pre-wrap",
              }}>
                {msg.content}
              </div>
            </div>
          ))}

          {loading && (
            <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
              <Avatar initials="IA" size={28} tone={T.brand} />
              <div style={{ fontSize: 12, color: T.textSub, fontStyle: "italic" }}>pensando...</div>
            </div>
          )}
        </div>

        {/* Input */}
        <div style={{ display: "flex", gap: 8, marginTop: 14, paddingTop: 14, borderTop: `1px solid ${T.border}` }}>
          <div style={{ flex: 1 }}>
            <Input
              value={pergunta}
              onChange={e => setPergunta(e.target.value)}
              onKeyDown={e => e.key === "Enter" && enviar()}
              placeholder="Pergunte algo sobre vendas, objeções, estratégias..."
            />
          </div>
          <Btn onClick={() => enviar()} icon={<Icon.send size={14} />} disabled={!pergunta.trim()}>Enviar</Btn>
        </div>
      </Card>
    </div>
  );
}

// ─── Mock de respostas ───────────────────────────────────────────────
function gerarRespostaMock(pergunta) {
  const p = pergunta.toLowerCase();

  if (p.includes("salão") || p.includes("salao")) {
    return "Para clientes de salão indecisos, comece identificando a principal dor: falta de espaço, conforto para clientes, ou imagem profissional. Depois, conecte cada solução Bellanor ao ROI real:\n\n• Bancada premium = aumenta o tempo médio do cliente no salão = mais receita\n• Lavatório confortável = Instagram-friendly = marketing orgânico\n• Balcão profissional = primeira impressão dos clientes novos\n\nUse perguntas como: 'Quantos clientes você perdeu por desconforto no último mês?' para criar urgência.";
  }

  if (p.includes("barbearia")) {
    return "Barbearias premium valorizam estética e status. Argumentos mais eficazes:\n\n• Ambientação vintage/moderna eleva o ticket médio em até 40%\n• Cadeira de qualidade = fidelização (cliente volta pelo conforto)\n• Espelhos barber style = fotos para redes sociais = marketing grátis\n\nDica: nunca venda só o móvel — venda a experiência completa que o cliente final terá.";
  }

  if (p.includes("clínica") || p.includes("clinica") || p.includes("estética") || p.includes("estetica")) {
    return "Clínicas estéticas decidem por credibilidade e regulamentação:\n\n1. Mobiliário clínico precisa atender ANVISA (mencione isso)\n2. Maca elétrica = mais procedimentos por dia = ROI em 4-6 meses\n3. Armário inox = profissionalismo visível = confiança do paciente\n\nTrabalhe com a proprietária(o) pensando em crescimento: 'Como é sua meta de faturamento nos próximos 12 meses?'";
  }

  if (p.includes("objeção") || p.includes("objecao") || p.includes("preço") || p.includes("preco") || p.includes("desconto")) {
    return "Nunca corte preço — corte prazo ou escopo. Técnicas:\n\n1. Divida o valor por mês de uso (móvel Bellanor dura 10+ anos)\n2. Compare com alternativa barata: 'o cliente final percebe a diferença'\n3. Ofereça parcelamento sem desconto\n4. Faça pacotes: 2 itens = frete grátis, 3 itens = brinde\n\nLembre: cada desconto reduz sua comissão. Melhor agregar valor do que diminuir preço.";
  }

  if (p.includes("roteiro") || p.includes("primeira conversa") || p.includes("abordagem")) {
    return "Roteiro Bellanor em 5 passos:\n\n1. **Quebra-gelo** (30s): 'Vi que seu espaço está em expansão, parabéns!'\n2. **Identificação de dor** (2min): 'O que te impede de atender mais clientes hoje?'\n3. **Apresentação ancorada** (3min): 'Baseado no que você disse, estes 3 itens resolvem 80% do problema'\n4. **Prova social**: 'O Studio Bella viveu exatamente isso — hoje ela fatura 40% a mais'\n5. **Call to action**: 'Posso te mandar uma simulação personalizada até amanhã?'\n\nTempo total: 8-10min. Nunca mais que isso na primeira conversa.";
  }

  return "Boa pergunta! Essa é uma resposta de demonstração — quando o Claude API estiver integrado, você terá respostas personalizadas baseadas no contexto real da Bellanor, histórico do cliente, e melhores práticas do time comercial.\n\nPor ora, tente reformular com palavras-chave como 'salão', 'barbearia', 'clínica', 'objeção', 'preço', ou 'roteiro' para ver exemplos pré-configurados.";
}
